import React, { useRef, useState } from 'react';
import phonepeImg from '../assets/phonpe.png';
import gpayImg from '../assets/gpay.png';
import paytmImg from '../assets/paytm.png';

const SOURCES = [
    { key: 'phonepe', label: 'PhonePe', img: phonepeImg },
    { key: 'gpay', label: 'GPay', img: gpayImg },
    { key: 'paytm', label: 'Paytm', img: paytmImg },
];

function detectSourceFromFile(file) {
    const name = (file?.name || '').toLowerCase();
    if (name.includes('phonepe') || name.includes('phone pe') || name.includes('pp_')) return 'phonepe';
    if (name.includes('gpay') || name.includes('google') || name.includes('tez')) return 'gpay';
    if (name.includes('paytm')) return 'paytm';
    return null;
}

/**
 * Big drag-and-drop PDF target for PhonePe / GPay / Paytm.
 * loadedSources: { phonepe: bool, gpay: bool, paytm: bool }
 * showSourcePicker: false on landing (chips only when filename is ambiguous).
 */
export default function StatementDropzone({
    onFile,
    compact = false,
    disabled = false,
    statusText = '',
    loadedSources = null,
    showSourcePicker = true,
}) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [source, setSource] = useState('phonepe');
    const [pendingFile, setPendingFile] = useState(null);
    const [needSource, setNeedSource] = useState(false);

    const emit = (src, file) => {
        if (!file || disabled) return;
        onFile?.(src, file);
    };

    const acceptFile = (file) => {
        if (!file) return;
        if (!file.name?.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
            return;
        }
        const detected = detectSourceFromFile(file);
        if (detected) {
            setSource(detected);
            emit(detected, file);
            return;
        }
        // Landing: no chip row — ask once. In-app with picker: use selected source.
        if (!showSourcePicker) {
            setPendingFile(file);
            setNeedSource(true);
            return;
        }
        emit(source, file);
    };

    const confirmSource = (src) => {
        setSource(src);
        setNeedSource(false);
        if (pendingFile) emit(src, pendingFile);
        setPendingFile(null);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        if (disabled) return;
        const file = e.dataTransfer?.files?.[0];
        acceptFile(file);
    };

    return (
        <div className={`statement-dropzone-wrap ${compact ? 'is-compact' : ''} ${!showSourcePicker ? 'is-simple' : ''}`}>
            {showSourcePicker && (
                <>
                    <div className="source-chips" role="radiogroup" aria-label="Which app is this PDF from?">
                        {SOURCES.map(s => {
                            const loaded = loadedSources?.[s.key];
                            return (
                                <button
                                    key={s.key}
                                    type="button"
                                    role="radio"
                                    aria-checked={source === s.key}
                                    className={`source-chip ${source === s.key ? 'active' : ''} ${loaded ? 'loaded' : ''}`}
                                    onClick={() => setSource(s.key)}
                                    disabled={disabled}
                                >
                                    <img src={s.img} alt="" />
                                    <span>{s.label}</span>
                                    {loaded ? <span className="source-check" aria-label="loaded">✓</span> : null}
                                </button>
                            );
                        })}
                    </div>
                    <p className="dropzone-pick-hint">Pick app, then PDF</p>
                </>
            )}

            <div
                className={`statement-dropzone ${dragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
                onDragEnter={e => { e.preventDefault(); if (!disabled) setDragging(true); }}
                onDragOver={e => { e.preventDefault(); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => { if (!disabled) inputRef.current?.click(); }}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (!disabled) inputRef.current?.click();
                    }
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    hidden
                    onChange={e => {
                        acceptFile(e.target.files?.[0]);
                        e.target.value = '';
                    }}
                />
                <div className="dropzone-mark">PDF</div>
                <p className="dropzone-title">
                    {compact
                        ? `Add ${SOURCES.find(s => s.key === source)?.label || ''} statement`
                        : 'Drop your statement'}
                </p>
                <p className="dropzone-sub">
                    {showSourcePicker ? 'Merges without double-counting' : 'PhonePe, GPay, or Paytm'}
                </p>
                {statusText && <p className="dropzone-status">{statusText}</p>}
            </div>

            {needSource && (
                <div className="sp-modal-overlay" style={{ zIndex: 1250 }} onClick={() => { setNeedSource(false); setPendingFile(null); }}>
                    <div className="sp-modal" onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>Which app is this PDF from?</h3>
                        <p style={{ color: 'var(--text-muted)', marginBottom: 20, fontSize: 14 }}>
                            We could not tell from the file name. Pick one — your data stays on this phone.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {SOURCES.map(s => (
                                <button
                                    key={s.key}
                                    type="button"
                                    className="sp-btn-ghost"
                                    style={{ justifyContent: 'flex-start', display: 'flex', gap: 12, alignItems: 'center' }}
                                    onClick={() => confirmSource(s.key)}
                                >
                                    <img src={s.img} alt="" width={28} height={28} style={{ borderRadius: 6 }} />
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
